// Apertura delle modali crea post e vedi post sopra la pagina corrente
import { isPlatformBrowser, Location } from '@angular/common';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { defer, filter, from, map, mergeMap, Observable, of, Subject, tap } from 'rxjs';
import { Post } from 'src/types/graphql';
import { NotLoggedService } from '../../../common/not-logged/not-logged.service';
import { OpenDialogService } from '../../../common/open-dialog/open-dialog.service';
import { RouteTranslateService } from '../../../common/route-translate/route-translate.service';
import { getDefaultModalSettings } from '../../../common/utils/helpers';
import type { PostEditorData } from '../../dialogs/post-editor-dialog/post-editor.form';
import type { PostViewResult } from '../../dialogs/post-view-dialog/post-view-dialog.component';

export const POST_QUERY_PARAM = 'post';

@Injectable({ providedIn: 'root' })
export class PostDialogsService {
  private readonly dialogs = inject(OpenDialogService);
  private readonly notLogged = inject(NotLoggedService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly routes = inject(RouteTranslateService);
  private readonly i18n = inject(I18NEXT_SERVICE);

  readonly saved$ = new Subject<Post>();
  readonly viewChanged$ = new Subject<PostViewResult>();

  private openSlug: string | null = null;

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.router.parseUrl(this.router.url).queryParamMap.get(POST_QUERY_PARAM)),
        filter(slug => !!slug && slug !== this.openSlug),
      )
      .subscribe(slug => this.openView(slug).subscribe());
  }

  openCreate(data: PostEditorData = {}): void {
    this.notLogged.execute(() =>
      this.editor(data).subscribe(post => {
        if (!post) return;
        const myPosts = this.routes.translateRoute('community/my-posts');
        if (!this.router.url.startsWith(myPosts)) this.router.navigateByUrl(myPosts);
      }),
    );
  }

  openEdit(post: Post): Observable<Post | undefined> {
    return this.editor({ post });
  }

  openView(slug: string): Observable<PostViewResult | undefined> {
    return defer(() => {
      this.openSlug = slug;
      this.setPostParam(slug);
      return from(import('../../dialogs/post-view-dialog/post-view-dialog.component'));
    }).pipe(
      mergeMap(({ PostViewDialogComponent }) => {
        const ref = this.dialogs.openDialog(PostViewDialogComponent, {
          ...this.config('post-view.header', 'Post di'),
          data: { slug },
        });
        return ref ? ref.afterClosed() : of(undefined);
      }),
      tap(result => {
        if (this.openSlug === slug) this.openSlug = null;
        if (!result?.navigated) this.setPostParam(null);
        if (result?.deleted || result?.updated) this.viewChanged$.next(result);
      }),
    );
  }

  private setPostParam(slug: string | null): void {
    const tree = this.router.parseUrl(this.location.path());
    const { [POST_QUERY_PARAM]: current, ...params } = tree.queryParams;
    if ((current ?? null) === slug) return;
    tree.queryParams = slug ? { ...params, [POST_QUERY_PARAM]: slug } : params;
    this.location.replaceState(this.router.serializeUrl(tree), '', this.location.getState());
  }

  private editor(data: PostEditorData): Observable<Post | undefined> {
    return from(import('../../dialogs/post-editor-dialog/post-editor-dialog.component')).pipe(
      mergeMap(({ PostEditorDialogComponent }) => {
        const ref = this.dialogs.openDialog(PostEditorDialogComponent, {
          ...this.config(
            data.post ? 'post-editor.header--edit' : 'post-editor.header',
            data.post ? 'Modifica post' : 'Crea post',
          ),
          data,
        });
        return ref ? ref.afterClosed() : of(undefined);
      }),
      map(post => post as Post | undefined),
      tap(post => post && this.saved$.next(post)),
    );
  }

  private config(labelKey: string, defaultValue: string): MatDialogConfig {
    return {
      ...getDefaultModalSettings(),
      maxWidth: '600px',
      ariaLabel: this.i18n.t(labelKey, { defaultValue }),
    };
  }
}
